import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { MessagingOutageSettings } from "@shared/schema";

type MessagingOutageSettingsResponse = {
  settings: MessagingOutageSettings | null;
};

export function MessagingOutagePanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery<MessagingOutageSettingsResponse>({
    queryKey: ["/api/admin/settings/messaging-outage"],
    queryFn: async () => {
      const res = await fetch("/api/admin/settings/messaging-outage", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load messaging outage status");
      return res.json();
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      setEnabled(settingsQuery.data.settings?.enabled === true);
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (next: boolean) =>
      apiRequest("PUT", "/api/admin/settings/messaging-outage", { enabled: next }),
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/messaging-outage"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/messaging-outage"] });
      toast({
        title: next ? "Outage banner enabled" : "Outage banner disabled",
        description: next
          ? "Every host now sees the PadSplit outage banner instead of the compose box."
          : "Hosts can compose and send messages normally again.",
      });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to save",
        description: parseErrorMessage(err, "Couldn't save. Please try again."),
        variant: "destructive",
      }),
  });

  const currentDb = settingsQuery.data?.settings?.enabled === true;
  const dirty = enabled !== currentDb;
  const updatedBy = settingsQuery.data?.settings?.updatedBy ?? null;
  const updatedAt = settingsQuery.data?.settings?.updatedAt ?? null;

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="p-5 space-y-5">
        <div>
          <h3 className="font-semibold text-sm mb-1" data-testid="text-messaging-outage-title">
            PadSplit Messaging Outage Banner
          </h3>
          <p className="text-xs text-muted-foreground">
            When enabled, every host's outbound message compose box is replaced app-wide with a
            temporary-outage banner directing them to send messages directly through PadSplit. Outbound
            sends are also blocked server-side while this is on. Changes take effect immediately — no
            redeploy required.
          </p>
        </div>

        {settingsQuery.isLoading ? (
          <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border px-3 py-3">
              <div className="space-y-0.5">
                <Label className="text-sm flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-brand" />
                  Outage banner active
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Replaces all compose boxes with the PadSplit outage banner.
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="switch-messaging-outage"
              />
            </div>

            <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-xs space-y-1" data-testid="status-messaging-outage">
              <p>
                <span className="text-muted-foreground">Current status:</span>{" "}
                <span data-testid="text-messaging-outage-current">
                  {currentDb ? "Outage banner is LIVE" : "Normal — messaging enabled"}
                </span>
              </p>
              {updatedBy && (
                <p className="text-muted-foreground">
                  Last changed by {updatedBy}
                  {updatedAt ? ` on ${new Date(updatedAt).toLocaleString()}` : ""}
                </p>
              )}
            </div>

            <Button
              className="w-full"
              onClick={() => saveMutation.mutate(enabled)}
              disabled={saveMutation.isPending || !dirty}
              data-testid="button-save-messaging-outage"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {enabled ? "Enable Outage Banner" : "Disable Outage Banner"}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
